import { Icon } from './Icon';
import { ccVar } from '../lib/css';
import { DIY_SCENES, type Tones } from './diyMarkScenes';

/** The mark for a platform that has no real brand logo (SEO, Web Scraping,
 *  the enrichment tools). Same box contract as `BrandIconChip`. */
export interface DiyMarkProps {
  /** catalog platform id, e.g. `seo`, `web`, `email-finding` */
  id: string;
  /** the platform's fallback HugeIcons name from the catalog */
  icon: string;
  /** category color token, the fallback when the platform has no hue of its own */
  color: string;
  /** box size in px */
  size: number;
  /** glyph size in px, for the fallback glyph */
  iconSize: number;
  /** corner radius in px (default 10) */
  radius?: number;
  /** Draw the scene alone at `size`, with no chip box behind it, because the
   *  caller already owns the surface it sits on. */
  bare?: boolean;
}

/** Each DIY platform gets its own hue, the way a brand mark carries its own
 *  color, so a section of DIY tools does not read as one flat wash of the
 *  category color. Spread so neighbours in one section differ. */
export const DIY_HUE: Readonly<Record<string, string>> = {
  seo: 'var(--c-blue)',
  web: 'var(--c-teal)',
  email: 'var(--c-amber)',
  'email-finding': 'var(--c-amber)',
  'email-verification': 'var(--c-green)',
  'mobile-phone': 'var(--c-violet)',
  person: 'var(--c-pink)',
  'person-enrichment': 'var(--c-violet)',
  'people-search': 'var(--c-pink)',
  'company-enrichment': 'var(--c-blue)',
  'company-search': 'var(--c-teal)',
  technographics: 'var(--c-slate)',
  'job-search': 'var(--c-amber)',
  social: 'var(--c-pink)',
  congress: 'var(--c-green)',
  sec: 'var(--c-slate)',
};


/**
 * Every paint is a `color-mix` of the category colour with a theme token, so a
 * scene keeps the same contrast in light and dark without a second palette:
 * `ink` leans on `--text` (dark on light, light on dark) and `pale` on
 * `--surface`, which is the tile the pictogram sits on.
 */
const tonesFor = (color: string): Tones => ({
  ink: `color-mix(in srgb, ${color} 72%, var(--text))`,
  mid: color,
  pale: `color-mix(in srgb, ${color} 34%, var(--surface))`,
  paper: 'var(--surface)',
  good: 'var(--c-green)',
  check: 'color-mix(in srgb, var(--c-green) 8%, white)',
});

/**
 * A mini flat illustration of the artifact the platform returns -
 * a ranked results page for SEO, a browser turning into braces for web
 * scraping, a contact card for people data - so a DIY tile carries as much
 * shape as the brand logo sitting next to it. Ids without a scene fall back to
 * the catalog's HugeIcons glyph.
 */
export const DiyMark = ({
  id,
  icon,
  color: fallback,
  size,
  iconSize,
  radius = 10,
  bare = false,
}: DiyMarkProps) => {
  const scene = DIY_SCENES[id];
  const color = DIY_HUE[id] ?? fallback;
  const art = bare ? size : Math.round(size * 0.74);
  const glyph = scene ? (
    <svg viewBox="0 0 24 24" width={art} height={art} aria-hidden="true" focusable="false">
      {scene(tonesFor(color))}
    </svg>
  ) : (
    <Icon name={icon} size={iconSize} />
  );
  if (bare) return glyph;
  return (
    <div
      className="cc-chip grid shrink-0 place-items-center"
      style={{ ...ccVar(color), width: size, height: size, borderRadius: radius }}
    >
      {glyph}
    </div>
  );
};
